import { useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { Noop } from 'types/common.interface';

import buttonDisabledVip from 'assets/wheel-of-fortune/button-disabled-vip.png';
import buttonDisabled from 'assets/wheel-of-fortune/button-disabled.png';
import buttonVip from 'assets/wheel-of-fortune/button-vip.png';
import button from 'assets/wheel-of-fortune/button.png';
import lampsDark from 'assets/wheel-of-fortune/lamps-dark.svg';
import lampsVip from 'assets/wheel-of-fortune/lamps-vip.svg';
import lamps from 'assets/wheel-of-fortune/lamps.svg';
import pointer from 'assets/wheel-of-fortune/pointer.svg';
import rmv from 'assets/wheel-of-fortune/rmv.png';

import { getQuantity, getRotationDegrees } from '../utils';
import WheelOfFortuneCanvas from '../WheelOfFortuneCanvas/WheelOfFortuneCanvas';
import { WHEEL_PALETTES } from '../wheelTheme';
import { RotationContainer, RouletteContainer } from './styles';
import { WheelData } from './types';

interface WheelProps {
  loading: boolean;
  prizeNumber: number | undefined;
  data: WheelData[];
  onSpin: Noop;
  onStopSpinning: Noop;
  disabled: boolean;
  variant?: 'default' | 'dark' | 'vip';
}

const FONT_SIZE = 30;
const FONT_WEIGHT = '500';
const FONT_STYLE = 'normal';
const TEXT_DISTANCE = 80;
const SPIN_DURATION = 1.0;

const STARTED_SPINNING = 'started-spinning';

const START_SPINNING_TIME = 2600;
const CONTINUE_SPINNING_TIME = 750;
const STOP_SPINNING_TIME = 8000;

const WheelOfFortune = ({
  loading,
  prizeNumber,
  data,
  onSpin,
  onStopSpinning,
  disabled,
  variant = 'default',
}: WheelProps) => {
  const [wheelData, setWheelData] = useState<WheelData[]>([...data]);
  const [prizeMap, setPrizeMap] = useState<number[][]>([[0]]);
  const [startRotationDegrees, setStartRotationDegrees] = useState(15);
  const [finalRotationDegrees, setFinalRotationDegrees] = useState(0);
  const [hasStartedSpinning, setHasStartedSpinning] = useState(false);
  const [hasStoppedSpinning, setHasStoppedSpinning] = useState(false);
  const [isCurrentlySpinning, setIsCurrentlySpinning] = useState(false);
  const [isDataUpdated, setIsDataUpdated] = useState(false);
  const [rouletteUpdater, setRouletteUpdater] = useState(false);
  const [loadedImagesCounter, setLoadedImagesCounter] = useState(0);
  const [totalImages, setTotalImages] = useState(0);
  const mustStopSpinning = useRef<boolean>(false);
  const animationClassKey = useRef<string>(crypto.randomUUID());

  const normalizedSpinDuration = Math.max(0.01, SPIN_DURATION);

  const startSpinningTime = START_SPINNING_TIME * normalizedSpinDuration;
  const continueSpinningTime = CONTINUE_SPINNING_TIME * normalizedSpinDuration;
  const stopSpinningTime = STOP_SPINNING_TIME * normalizedSpinDuration;

  const totalSpinningTime = startSpinningTime + continueSpinningTime + stopSpinningTime;
  const isDarkVariant = variant === 'dark';
  const isVipVariant = variant === 'vip';
  const palette = isVipVariant ? WHEEL_PALETTES.vip : WHEEL_PALETTES.regular;
  // The frame (ring + lamps) is an image, so the VIP version is its own asset rather than
  // a filter: lamps-vip.svg is lamps.svg with the two blues swapped for violet and gold.
  // Its bulbs were already gold, which is why only the rings needed recolouring.
  const frame = isVipVariant ? lampsVip : isDarkVariant ? lampsDark : lamps;
  // The GO button is artwork, not CSS: the stock one is teal with a blue rim, which was
  // the last blue left on the VIP wheel. The -vip pair is the same art hue-shifted to
  // purple with a magenta rim, matching the frame ring.
  const goImage = isVipVariant ? buttonVip : button;
  const goImageDisabled = isVipVariant ? buttonDisabledVip : buttonDisabled;

  useEffect(() => {
    let cancelled = false;
    let initialMapNum = 0;
    const auxPrizeMap: number[][] = [];
    const dataLength = data?.length || 0;
    const wheelDataAux = [{ option: '', optionSize: 1 }] as WheelData[];

    const imagePromises: Promise<void>[] = [];

    for (let i = 0; i < dataLength; i++) {
      wheelDataAux[i] = {
        ...data[i],
        textColor: hasStoppedSpinning && i === prizeNumber ? palette.highlightText : palette.text,
      };
      auxPrizeMap.push([]);

      for (let j = 0; j < (wheelDataAux[i].optionSize || 1); j++) {
        auxPrizeMap[i][j] = initialMapNum++;
      }

      // The API sends an icon per prize, so a wheel can show lootboxes and spins rather
      // than only the two coins this used to choose between.
      const coin = {
        uri: data[i]?.iconUrl || rmv,
        offset: -100,
        size: 80,
      };

      imagePromises.push(
        new Promise<void>((resolve) => {
          const img = new Image();
          img.src = coin.uri;
          img.onload = () => {
            img.height = coin.size;
            img.width = coin.size;
            wheelDataAux[i].image = { ...coin, _imageHTML: img };
            resolve();
          };
          img.onerror = () => resolve();
        })
      );
    }

    setTotalImages(dataLength);

    Promise.all(imagePromises).then(() => {
      if (cancelled) return;
      setWheelData([...wheelDataAux]);
      setPrizeMap(auxPrizeMap);
      setLoadedImagesCounter(dataLength);
      setIsDataUpdated(true);
    });

    return () => {
      cancelled = true;
    };
  }, [data, palette]);

  useEffect(() => {
    if (prizeNumber !== undefined && !isCurrentlySpinning) {
      // Reset hasStoppedSpinning when starting a new spin to prevent border from showing
      setHasStoppedSpinning(false);
      setIsCurrentlySpinning(true);
      startSpinning();

      const finalRotationDegreesCalculated = getRotationDegrees(prizeNumber, getQuantity(prizeMap));
      setFinalRotationDegrees(finalRotationDegreesCalculated);
    } else if (prizeNumber === undefined) {
      // Reset states when prizeNumber is cleared
      setHasStoppedSpinning(false);
      setIsCurrentlySpinning(false);
    }
  }, [prizeNumber]);

  useEffect(() => {
    if (hasStoppedSpinning) {
      setIsCurrentlySpinning(false);
      setStartRotationDegrees(finalRotationDegrees);
    }
  }, [hasStoppedSpinning]);

  const startSpinning = () => {
    // Generate new class key only when starting new spin to avoid animation resets
    animationClassKey.current = crypto.randomUUID();
    setHasStartedSpinning(true);
    setHasStoppedSpinning(false);
    mustStopSpinning.current = true;

    setTimeout(() => {
      if (mustStopSpinning.current) {
        mustStopSpinning.current = false;
        setHasStartedSpinning(false);
        setHasStoppedSpinning(true);
        onStopSpinning();
      }
    }, totalSpinningTime);
  };

  const getRouletteClass = () => {
    if (hasStartedSpinning) {
      return STARTED_SPINNING;
    }
    return '';
  };

  if (!isDataUpdated) {
    return null;
  }

  return (
    <>
      <RouletteContainer
        style={loading || (totalImages > 0 && loadedImagesCounter !== totalImages) ? { visibility: 'hidden' } : {}}
      >
        <button
          onClick={onSpin}
          className={twMerge(
            'tw-pointer-events-auto tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-top-0 tw-z-30 tw-m-auto tw-flex tw-h-20 tw-w-20 tw-items-center tw-justify-center tw-rounded-full tw-border-none tw-bg-[length:110px] tw-bg-center tw-text-2xl tw-font-bold tw-text-white md:tw-h-28 md:tw-w-28 md:tw-bg-[length:152px] md:tw-text-4xl',
            !disabled && !hasStartedSpinning && 'tw-cursor-pointer'
          )}
          disabled={disabled || hasStartedSpinning}
          style={{
            backgroundImage: `url(${disabled || hasStartedSpinning ? goImageDisabled : goImage})`,
            textShadow: disabled || hasStartedSpinning ? '0px 4px 4px rgba(0, 0, 0, 0.25)' : 'none',
          }}
        >
          GO
        </button>
        <RotationContainer
          className={getRouletteClass()}
          $classKey={animationClassKey.current}
          $startSpinningTime={startSpinningTime}
          $continueSpinningTime={continueSpinningTime}
          $stopSpinningTime={stopSpinningTime}
          $startRotationDegrees={startRotationDegrees}
          $finalRotationDegrees={finalRotationDegrees}
        >
          <WheelOfFortuneCanvas
            width="900"
            height="900"
            data={wheelData}
            fontWeight={FONT_WEIGHT}
            fontStyle={FONT_STYLE}
            fontSize={FONT_SIZE}
            prizeMap={prizeMap}
            rouletteUpdater={rouletteUpdater}
            textDistance={TEXT_DISTANCE}
            prizeNumber={prizeNumber}
            hasStoppedSpinning={hasStoppedSpinning}
            hasStartedSpinning={hasStartedSpinning}
            variant={variant}
            palette={palette}
          />
        </RotationContainer>
        <img
          src={pointer}
          alt="pointer"
          className="tw-absolute tw-left-[calc(50%_+_-16px)] tw-top-[calc(50%_-_62px)] tw-w-8 md:tw-left-[calc(50%_+_-36px)] md:tw-top-[calc(50%_-_105px)] md:tw-w-[71px]"
        />
        <div
          className="tw-absolute tw-inset-0 tw-bg-cover tw-bg-center tw-bg-no-repeat"
          style={{ backgroundImage: `url(${frame})` }}
        />
      </RouletteContainer>
    </>
  );
};

export default WheelOfFortune;
